/**
 * Detector heurístico de info de contacto en texto libre.
 * Usado para warning al postear/comentar en comunidad y proteger el modelo
 * de negocio (que el cliente no consiga datos directos del Guru y haga la
 * sesión por fuera de Gurumba sin pagar comisión).
 *
 * NO bloquea — solo avisa. El user puede insistir si es falso positivo
 * (ej: "Mi MRR fue $1000" no es teléfono pero podría matchear).
 */

export interface DetectionResult {
  hasMatches: boolean;
  reasons: string[];        // labels human-readable
  matches: Array<{ type: string; sample: string }>;
}

// Email: clásico, robusto
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

// Teléfono internacional: opcional +, 8+ dígitos (incluye espacios/guiones/paréntesis)
// Ej: +54 11 5555 1234, (305) 555-0188, +5491155551234
const PHONE_RE = /(\+?\d[\d\s\-()]{7,}\d)/g;

// Palabra que delata intención de contacto (para afinar el match de teléfono y
// no marcar montos/años largos como si fueran un número telefónico).
const CONTACT_WORD_RE = /(ll[aá]mame|escr[ií]beme|cont[aá]ctame|whats\s?app|telegram|signal|mi\s+(cel|celular|n[uú]mero|tel|whats|wsp))/i;

// URL externa (excluyendo gurumba.com)
const URL_RE = /https?:\/\/(?!.*gurumba\.com)([^\s]+)/gi;

// NOTA: antes había una regla HANDLE_RE que marcaba cualquier "@palabra" como
// "handle de red social". Desde que existen las @menciones a usuarios de
// Gurumba, eso generaba falsos positivos (mencionar a alguien rompía el check).
// Se quitó: los vectores reales de fuga (email, teléfono, URL externa, apps de
// mensajería, email/número en palabras) siguen cubiertos abajo. Un "@x" suelto
// no da, por sí solo, una vía de contacto fuera de la plataforma.

// Menciones de plataformas de contacto directo
const PLATFORM_RE = /\b(whatsapp|whats\s?app|telegram|signal|skype|wechat|messenger|imessage|sms|llamame|llámame|cont(á|a)ctame|escr(í|i)beme|insta(gram)?|linkedin|li(?:nk)?\.in|tw(itter)?|x\.com)\b/gi;

// Numerales en palabras español (cero..diez). Detectamos 5+ consecutivos
// con separadores opcionales (espacios, guiones, comas, "y").
// Ejemplo: "cinco tres dos uno cinco cero cuatro" (8 dígitos → teléfono).
const NUMERAL_WORDS = '(cero|uno|una|dos|tres|cuatro|cinco|seis|siete|ocho|nueve|diez)';
const NUMERAL_SEQ_RE = new RegExp(
  `\\b${NUMERAL_WORDS}(?:[\\s\\-,]+(?:y\\s+)?${NUMERAL_WORDS}){4,}\\b`,
  'gi'
);

// Email enmascarado: "X arroba Y punto Z"
const MASKED_EMAIL_RE = /\b\w+\s+arroba\s+\w+\s+punto\s+\w+/gi;

export function detectContactInfo(text: string): DetectionResult {
  if (!text || text.length < 3) {
    return { hasMatches: false, reasons: [], matches: [] };
  }

  const matches: Array<{ type: string; sample: string }> = [];
  const reasons: string[] = [];

  const emails = text.match(EMAIL_RE);
  if (emails && emails.length > 0) {
    reasons.push('email');
    emails.forEach((m) => matches.push({ type: 'email', sample: m }));
  }

  // Teléfono: para no marcar montos/años largos, exigimos SEÑAL de teléfono:
  //   - un '+' adelante (internacional), o
  //   - formato agrupado tipo "11 5555 1234" (separadores + 9+ dígitos), o
  //   - una palabra de contacto en el texto ("llamame", "mi cel", "wsp"…).
  const hasContactWord = CONTACT_WORD_RE.test(text);
  const phones = (text.match(PHONE_RE) ?? []).filter((m) => {
    const digits = m.replace(/\D/g, '');
    if (digits.length < 8) return false;
    const hasPlus = m.trim().startsWith('+');
    const looksGrouped = /[\s\-().]/.test(m) && digits.length >= 9;
    return hasPlus || looksGrouped || hasContactWord;
  });
  if (phones.length > 0) {
    reasons.push('teléfono');
    phones.forEach((m) => matches.push({ type: 'teléfono', sample: m }));
  }

  const urls = text.match(URL_RE);
  if (urls && urls.length > 0) {
    reasons.push('link externo');
    urls.forEach((m) => matches.push({ type: 'url', sample: m }));
  }

  const platforms = text.match(PLATFORM_RE);
  if (platforms && platforms.length > 0) {
    reasons.push('app de mensajería directa');
    platforms.forEach((m) => matches.push({ type: 'platform', sample: m }));
  }

  const numeralSeqs = text.match(NUMERAL_SEQ_RE);
  if (numeralSeqs && numeralSeqs.length > 0) {
    reasons.push('número en palabras');
    numeralSeqs.forEach((m) => matches.push({ type: 'numeral_seq', sample: m }));
  }

  const maskedEmails = text.match(MASKED_EMAIL_RE);
  if (maskedEmails && maskedEmails.length > 0) {
    reasons.push('email en palabras');
    maskedEmails.forEach((m) => matches.push({ type: 'masked_email', sample: m }));
  }

  return {
    hasMatches: matches.length > 0,
    reasons: [...new Set(reasons)],
    matches,
  };
}

/** Mensaje corto para mostrar al user. La publicación se bloquea, no hay override. */
export function contactWarningMessage(_result: DetectionResult): string {
  return 'Detectamos info de contacto en tu post. La comunidad es para conversación pública y, según las normas del sitio, esto no está permitido.';
}
